export interface EquivalentsRow {
  qty: string;
  desc: string;
  vol: string;
}

export interface ParsedEquivalents {
  headline: string | null;
  rows: EquivalentsRow[];
}

// Some count questions (e.g. alcohol intake) author subtitleHtml as a
// "1 drink equals ..." sentence describing what one unit represents, rather
// than a plain hint, e.g. "1 alcoholic drink equals 1 small glass of wine
// [5 oz/125 ml], 1 can/bottle of beer [12 oz/330 ml], or 1 shot of
// spirits/liquor [1.5 oz/35 ml]".
//
// Every row is "<count> <description> [<measure>]" in every locale; only the
// prose between rows (the verb, the conjunction, the list punctuation) and
// the decimal separator inside the measure vary. So a row is recognised by
// its bracketed measure and the count that precedes its description, never
// by the words or commas around it — a comma-decimal measure such as
// "[1,5 oz/35 ml]" cannot end a row early.
const ROW = /(\d+)\s+([^[\]\d]+?)\s*\[([^\]]+)\]/g;

export const parseEquivalents = (
  subtitleHtml: string
): ParsedEquivalents | null => {
  const plain = subtitleHtml.replace(/<[^>]+>/g, "").trim();

  const rows: EquivalentsRow[] = [];
  let firstRowIndex: number | null = null;
  let match: RegExpExecArray | null;
  ROW.lastIndex = 0;
  while ((match = ROW.exec(plain)) !== null) {
    if (firstRowIndex === null) firstRowIndex = match.index;
    rows.push({
      qty: `${match[1]}x`,
      desc: match[2].trim(),
      vol: match[3].trim(),
    });
  }

  // One bracketed measure is not a table; the caller shows the subtitle as a
  // plain hint instead.
  if (firstRowIndex === null || rows.length < 2) {
    return null;
  }

  const headline = plain.slice(0, firstRowIndex).trim();
  return { headline: headline || null, rows };
};
